import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/errors";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import TrackerStats from "../components/tracker/TrackerStats";
import BankrollChart from "../components/tracker/BankrollChart";
import BetForm from "../components/tracker/BetForm";
import BetTable from "../components/tracker/BetTable";
import ShareBankrollCard from "../components/ShareBankrollCard";
import { useAuth } from "../context/AuthContext";

const EMPTY_FORM = {
  sport: "mlb",
  matchup: "",
  pick: "",
  stake: 10,
  american_odds: -110,
  result: "pending",
  step: 1,
  variant: "classic",
  notes: "",
};

export default function Tracker() {
  const { user } = useAuth();
  const [bets, setBets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/bets"), api.get("/bets/stats")]);
      setBets(b.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bets", {
        ...form,
        stake: Number(form.stake),
        american_odds: Number(form.american_odds),
        step: Number(form.step),
      });
      toast.success("Bet logged");
      setForm((f) => ({ ...f, pick: "", matchup: "", notes: "" }));
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not log bet"));
    }
  };

  const remove = async (id) => {
    await api.delete(`/bets/${id}`);
    reload();
  };

  const resetAll = async () => {
    if (!window.confirm("Wipe all bet history? This cannot be undone.")) return;
    await api.post("/bets/reset");
    toast.success("Tracker reset");
    reload();
  };

  return (
    <div data-testid="tracker-page">
      <div className="border-b border-zinc-800 pb-10 mb-10 flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="overline mb-3">LEDGER</div>
          <h1 className="font-display text-4xl lg:text-5xl">The bet tracker.</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            Log every play. See your real bankroll curve. Know your current Martingale step
            at a glance.
          </p>
        </div>
        <button
          onClick={resetAll}
          data-testid="tracker-reset"
          className="flex items-center gap-2 px-4 py-2 border border-zinc-800 text-zinc-400 hover:border-red-500 hover:text-red-400 font-mono text-xs uppercase tracking-widest"
        >
          <RotateCcw size={14} strokeWidth={1.5} /> Reset Ledger
        </button>
      </div>

      <TrackerStats stats={stats} />
      {stats && <BankrollChart timeline={stats.timeline} />}
      <div className="mb-10">
        <ShareBankrollCard stats={stats} user={user} />
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <BetForm form={form} setForm={setForm} onSubmit={submit} />
        <div className="lg:col-span-8">
          <div className="overline mb-3">RECENT BETS</div>
          <BetTable bets={bets} loading={loading} onDelete={remove} />
        </div>
      </div>
    </div>
  );
}
